import { thunk } from 'easy-peasy';
import { ethers } from 'ethers';
import pick from 'lodash.pick';

import { notification } from 'antd';
import adminCommunitiesAPI from 'api/admin/adminCommunitiesAPI';
import tradingAPI from 'api/trading/tradingAPI';
import { AuctionContract, PlatformContract } from 'blockchain';
import { TradingContract } from 'blockchain/contracts/TradingContract';
import { CommunityStatus, ErrorTypes } from 'constants/index';
import { TransactionReceiptStatus } from 'constants/transaction';
import moment from 'moment';
import {
  AdminCommunitiesActionsAndThunks,
  IAdminCommunitiesThunks,
  IApproveCommunityPayload,
  IDeleteCommunityPayload,
  IFilterRequestParams,
  IMarkCommunityForDeletion,
  IRejectCommunityPayload,
  IStoreModel,
  TradeStatus,
} from 'types';
import { convertUnixTimestampToMiliseconds } from 'utils/convertUnixTimestamp';
import { validateWalletForTransaction } from 'utils/validateTransaction';
import { verifyTransaction } from 'utils/verifyTransaction';

export const thunks: IAdminCommunitiesThunks = {
  loadPendingCommunities: thunk<AdminCommunitiesActionsAndThunks, IFilterRequestParams, unknown, IStoreModel>(
    async (actions, payload) => {
      actions.startPendingCommunitiesLoading();
      try {
        const pendingCommunities = await adminCommunitiesAPI.getPendingCommunities(
          pick(payload, ['name', 'status', 'page', 'limit', 'createdAt']),
        );
        actions.setPendingCommunities(pendingCommunities);
      } finally {
        actions.stopPendingCommunitiesLoading();
      }
    },
  ),

  loadCommunityById: thunk<AdminCommunitiesActionsAndThunks, string | number, unknown, IStoreModel>(
    async (actions, payload) => {
      const community = await adminCommunitiesAPI.getCommunityById(payload);
      actions.setCommunity(community);
    },
  ),

  startCommunityReview: thunk<AdminCommunitiesActionsAndThunks, string, unknown, IStoreModel>(
    async (actions, payload) => {
      await adminCommunitiesAPI.updateCommunity(payload, { status: CommunityStatus.InReview });
    },
  ),

  approveCommunity: thunk<AdminCommunitiesActionsAndThunks, IApproveCommunityPayload, unknown, IStoreModel>(
    async (actions, payload, { getStoreState }) => {
      const wallet = getStoreState().blockchain.wallet;

      if (!payload.community.tokenAddr) {
        if (!wallet.provider) {
          notification.error({ message: 'No provider' });
          return;
        }

        const signer = await validateWalletForTransaction({ web3Provider: wallet.provider });

        if (!signer) {
          console.error('No valid signer available');
          return;
        }

        const contract = new PlatformContract(signer);
        const tx = await contract.registerCommunity(payload.community);

        const receipt = await tx.wait();

        if (receipt.status === TransactionReceiptStatus.Success) {
          await adminCommunitiesAPI.updateCommunity(payload.community.id, {
            status: CommunityStatus.Approved,
            txHash: tx.hash,
          });
        }
      } else {
        await adminCommunitiesAPI.updateCommunity(payload.community.id, {
          status: CommunityStatus.Approved,
        });
      }
    },
  ),

  rejectCommunity: thunk<AdminCommunitiesActionsAndThunks, IRejectCommunityPayload, unknown, IStoreModel>(
    async (actions, payload) => {
      await adminCommunitiesAPI.updateCommunity(payload.id, {
        status: CommunityStatus.Rejected,
        reason: payload.reason,
      });
    },
  ),

  deleteCommunity: thunk<AdminCommunitiesActionsAndThunks, IDeleteCommunityPayload, unknown, IStoreModel>(
    async (actions, payload, { getStoreState }) => {
      const wallet = getStoreState().blockchain.wallet;

      if (!wallet.provider) {
        notification.error({
          message: 'No provider',
        });
        return;
      }

      const signer = await validateWalletForTransaction({ web3Provider: wallet.provider });

      if (!signer) {
        console.error('No valid signer available');
        return;
      }
      const auctionContract = new AuctionContract(signer);

      const auction = await AuctionContract.getAuction(payload.accountAddress);
      const auctionStartTime = +ethers.BigNumber.from(auction.startTime).toString();

      // check if project has active auction
      if (
        auction.isValid &&
        auctionStartTime &&
        convertUnixTimestampToMiliseconds(Number(auctionStartTime)) > moment().valueOf()
      ) {
        const isSuccess = await verifyTransaction(auctionContract.cancelAuction(payload.accountAddress));

        if (!isSuccess) {
          throw new Error(ErrorTypes.DeleteAuction);
        }
      }

      const tradings = await tradingAPI.getList({
        userId: payload.userId,
        status: TradeStatus.Active,
      });

      const tradingItems = tradings.items;

      const tradingContract = new TradingContract(signer);

      if (tradingItems.length > 0) {
        const isSuccess = await verifyTransaction(
          tradingContract.batchDeleteSellBuy(Array.from(tradingItems, (item) => item.offerId)),
        );

        if (!isSuccess) {
          throw new Error(ErrorTypes.DeleteBids);
        }
      }

      const contract = new PlatformContract(signer);

      const communityInfo = await contract.printProjectInfo(payload.accountAddress);

      if (communityInfo?.length) {
        const isSuccess = await verifyTransaction(contract.removeProject(payload.accountAddress));

        if (!isSuccess) {
          throw new Error(ErrorTypes.DeleteProject);
        }
      }

      await adminCommunitiesAPI.deleteCommunity(payload.id);
    },
  ),
  checkIsCanDelete: thunk<AdminCommunitiesActionsAndThunks, number, unknown, IStoreModel>(async (actions, payload) => {
    const res = await adminCommunitiesAPI.checkIsCanDelete(payload);
    return res;
  }),

  markForDeletion: thunk<AdminCommunitiesActionsAndThunks, IMarkCommunityForDeletion, unknown, IStoreModel>(
    async (actions, payload) => {
      await adminCommunitiesAPI.markForDeletion(payload.id, {
        markedForDeletion: payload.markedForDeletion,
      });
    },
  ),
};
